import * as React from 'react';
import { Input } from '../../../components/forms/Input';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { Select } from '../../../components/forms/Select';
import { SettingsTeamMember, SettingsWorkspace } from '../../../models/settings-pages';
import { Checkbox } from '../../../components/forms/Checkbox';
import classes from './NewWorkspaceModal.module.scss';
import { TimezoneSetting, UserOrganizationRole } from '../../../models/graphql';
import { FormModal } from '../../../components/containers/FormModal';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { InputTypes } from '../../../constants/form';
import { mapTimezone } from '../../../helpers/map-timezone';

export type NewWorkspaceModalViewProps = {
  workspace?: SettingsWorkspace | null;
  members: SettingsTeamMember[] | undefined;
  onClose: () => void;
  submitted: boolean;
  onSubmit: SubmitHandler<{
    workspaceId: string;
    name: string;
    timezone: TimezoneSetting;
    member: [];
    invited: [];
  }>;
  nameRules: RegisterOptions;
  selectedTimezone: string | null;
  timezoneRules: RegisterOptions;
  noRules: RegisterOptions;
  useFormInstance: UseFormReturn;
};

export const NewWorkspaceModalView = (props: NewWorkspaceModalViewProps): JSX.Element => {
  const modalTitle = props.workspace ? 'Edit Workspace' : 'Add a Workspace';
  const buttonLabel = props.workspace ? 'Save Workspace' : 'Add Workspace';

  const capitalise = (value: string) => {
    return value.charAt(0).toUpperCase() + value.toLowerCase().slice(1);
  };

  const ListOfTeamMembers = props.members?.map(member => {
    const capitaliseRoleName: string = capitalise(member.role);
    const suffix: string = member.role !== UserOrganizationRole.EDITOR ? `(${capitaliseRoleName})` : '';
    const teamUserName: string = member.status !== 'invited' ? member.name : member.email;
    const isInWorkspace: boolean = member.workspaces?.includes(props.workspace?.id || '') || false;
    const isLastWorkspace: boolean = (member.workspaces?.length || 0) < 2 && isInWorkspace;

    return (
      <div key={`container-${member.status}-${member.id}`} className={'margin-top-small-8 margin-bottom-small-8'}>
        <Checkbox
          id={`checkbox-${member.id}-${member.status}`}
          formInstance={props.useFormInstance}
          name={`${member.status}[${member.id}]`}
          rules={props.noRules}
          size={'large'}
          label={`${teamUserName} ${suffix}`}
          readonly={member.role !== UserOrganizationRole.EDITOR || isLastWorkspace || props.submitted}
        />
      </div>
    );
  });

  const timeZoneListItems = mapTimezone();

  return (
    <FormModal
      onClose={props.onClose}
      showCloseIcon={!props.submitted}
      formInstance={props.useFormInstance}
      loading={props.submitted}
      onSubmit={props.onSubmit}
      submitText={buttonLabel}
      title={modalTitle}>
      <div className={'margin-bottom-small-16'}>
        {props.workspace?.id?.length && (
          <Input
            disabled={props.submitted}
            formInstance={props.useFormInstance}
            name={'workspaceId'}
            type={InputTypes.hidden}
            size={'small'}
            rules={props.noRules}
          />
        )}
        <Input
          disabled={props.submitted}
          formInstance={props.useFormInstance}
          name={'name'}
          type={InputTypes.text}
          size={'large'}
          label={`Name`}
          rules={props.nameRules}
        />
      </div>
      <div className={'margin-bottom-small-16'}>
        <Select
          items={timeZoneListItems}
          selectedValue={props.selectedTimezone || undefined}
          label={'Timezone'}
          name={'timezone'}
          size={'large'}
          rules={props.timezoneRules}
          useFormInstance={props.useFormInstance}
          disabled={props.submitted}
        />
      </div>
      <div>
        <div className={classes.Label}>Workspace Permissions</div>
        <div className={`grid-y ${classes.MembersContainer}`}>{ListOfTeamMembers}</div>
      </div>
    </FormModal>
  );
};
